import { Button } from "@arcadeai/design-system/components/ui/atoms/button";
import { Input } from "@arcadeai/design-system/components/ui/atoms/input";
import { useSearch } from "@tanstack/react-router";
import { Plus, Search } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { CanProject } from "@/app/capabilities/components/can-project";
import { PROJECT } from "@/app/capabilities/permissions";
import { AlphaBanner } from "@/components/custom/alpha-banner";
import { ListLayout } from "@/components/custom/list-layout/list-layout";
import { useSuspenseGetCatalog } from "./api/hooks";
import { CatalogAudienceManager } from "./components/catalog-audience-manager";
import { CatalogEmptyState } from "./components/catalog-empty-state";
import { CatalogGrantSheet } from "./components/catalog-grant-sheet";
import { EnduserShareCard } from "./components/enduser-share-card";
import { CATALOG_ROUTE_ID } from "./search-params";
import { filterCatalogBySearch } from "./utils";

export const CatalogList = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const { create } = useSearch({ from: CATALOG_ROUTE_ID });

  // The create sheet lives at the list level so it survives the empty ->
  // populated transition: publishing the first grant invalidates the list and
  // swaps the empty state for the table, which would otherwise unmount a sheet
  // owned by the empty-state subtree mid-flow.
  const [isCreateOpen, setIsCreateOpen] = useState(create);
  const openCreateSheet = useCallback(() => setIsCreateOpen(true), []);

  const { data: entries = [] } = useSuspenseGetCatalog();

  const filtered = useMemo(
    () => filterCatalogBySearch(entries, searchTerm),
    [entries, searchTerm]
  );

  return (
    <ListLayout
      actions={
        <CanProject permission={PROJECT.catalog.create}>
          <Button onClick={openCreateSheet} size="sm">
            <Plus className="mr-2 size-4" />
            Add grant
          </Button>
        </CanProject>
      }
      description="Publish which tools each IdP group — or everyone — can self-serve."
      filters={
        <div className="flex items-center gap-3">
          <div className="relative max-w-md">
            <Search className="absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              className="h-9 w-full pl-9"
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Search end-user tools..."
              value={searchTerm}
            />
          </div>
        </div>
      }
      title="End-user dashboard"
    >
      <div className="space-y-6">
        <AlphaBanner />
        <EnduserShareCard />
        {filtered.length > 0 ? (
          <CatalogAudienceManager entries={filtered} />
        ) : (
          <CatalogEmptyState
            isFiltered={entries.length > 0}
            onCreate={openCreateSheet}
          />
        )}
      </div>
      <CatalogGrantSheet onOpenChange={setIsCreateOpen} open={isCreateOpen} />
    </ListLayout>
  );
};
